import { LucideIcon, TrendingUp, TrendingDown, Minus } from 'lucide-react';
import { Card, CardContent } from '@respectly/ui';

interface UsageMetricCardProps {
  label: string;
  value: string | number;
  subLabel?: string;
  icon: LucideIcon;
  iconColor: string;
  iconBg: string;
  trend?: number; // positive = up, negative = down, undefined = no trend
  trendLabel?: string;
  isLoading?: boolean;
  formatter?: (v: number) => string;
}

export function UsageMetricCard({
  label,
  value,
  subLabel,
  icon: Icon,
  iconColor,
  iconBg,
  trend,
  trendLabel,
  isLoading,
}: UsageMetricCardProps) {
  if (isLoading) {
    return (
      <Card className="border-slate-200/60 dark:border-slate-700/60">
        <CardContent className="p-5">
          <div className="flex items-start justify-between">
            <div className="space-y-2 flex-1">
              <div className="h-3 w-24 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
              <div className="h-8 w-20 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
              <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
            </div>
            <div className="h-10 w-10 bg-slate-200 dark:bg-slate-700 rounded-lg animate-pulse" />
          </div>
        </CardContent>
      </Card>
    );
  }

  const trendPositive = trend !== undefined && trend > 0;
  const trendNegative = trend !== undefined && trend < 0;

  return (
    <Card className="border-slate-200/60 dark:border-slate-700/60 hover:shadow-md transition-shadow">
      <CardContent className="p-5">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider mb-1">
              {label}
            </p>
            <p className="text-3xl font-bold tabular-nums text-slate-900 dark:text-slate-100 leading-tight">
              {value}
            </p>
            {subLabel && (
              <p className="text-xs text-slate-500 dark:text-slate-400 mt-1">{subLabel}</p>
            )}
            {trend !== undefined && (
              <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${
                trendPositive ? 'text-emerald-600 dark:text-emerald-400' :
                trendNegative ? 'text-red-500 dark:text-red-400' :
                'text-slate-500 dark:text-slate-400'
              }`}>
                {trendPositive ? <TrendingUp className="h-3 w-3" /> :
                 trendNegative ? <TrendingDown className="h-3 w-3" /> :
                 <Minus className="h-3 w-3" />}
                <span>{trendLabel ?? (trend > 0 ? `+${trend}%` : `${trend}%`)}</span>
              </div>
            )}
          </div>
          <div className={`flex-shrink-0 h-10 w-10 rounded-lg ${iconBg} flex items-center justify-center`}>
            <Icon className={`h-5 w-5 ${iconColor}`} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
